import { supabase } from '../lib/supabase';
import { alertManagementService } from './alertManagementService';

const EDGE_FUNCTION_URL = import.meta.env?.VITE_SUPABASE_URL + '/functions/v1';

export const patternDetectionService = {
  // ============================================
  // CONFIGURATION MANAGEMENT
  // ============================================

  async getPatternConfig() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('pattern_detection_config')?.select('*')?.eq('user_id', user?.id)?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      // Return default config if none exists
      return data || {
        failed_auth_enabled: true,
        failed_auth_threshold: 5,
        failed_auth_window_minutes: 15,
        failed_auth_per_ip_enabled: true,
        failed_auth_per_user_enabled: true,
        location_detection_enabled: true,
        location_distance_threshold_km: 500,
        location_new_country_alert: true,
        location_risk_threshold: 70,
        bulk_permission_enabled: true,
        bulk_permission_threshold: 3,
        bulk_permission_window_minutes: 10,
        privilege_escalation_detection: true,
        auto_escalation_enabled: true,
        escalation_severity_threshold: 'high',
        escalation_sms_enabled: true,
        escalation_email_enabled: true,
        escalation_recipients: []
      };
    } catch (error) {
      console.error('Error fetching pattern config:', error);
      throw error;
    }
  },

  async updatePatternConfig(config) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('pattern_detection_config')?.upsert({
          user_id: user?.id,
          ...config,
          updated_at: new Date()?.toISOString()
        }, { onConflict: 'user_id' })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating pattern config:', error);
      throw error;
    }
  },

  // ============================================
  // FAILED AUTHENTICATION DETECTION
  // ============================================

  async trackFailedAuth(userId, ipAddress, deviceInfo, location, failureReason) {
    try {
      const { error } = await supabase?.from('failed_auth_tracking')?.insert({
          user_id: userId,
          ip_address: ipAddress,
          device_info: deviceInfo,
          location,
          failure_reason: failureReason,
          attempt_timestamp: new Date()?.toISOString()
        });

      if (error) throw error;

      // Check for pattern violations
      await this.checkFailedAuthPattern(userId, ipAddress);
    } catch (error) {
      console.error('Error tracking failed auth:', error);
      throw error;
    }
  },

  async checkFailedAuthPattern(userId, ipAddress) {
    try {
      const config = await this.getPatternConfig();
      if (!config?.failed_auth_enabled) return;

      const windowStart = new Date(Date.now() - config?.failed_auth_window_minutes * 60000)?.toISOString();

      // Check per-user pattern
      if (config?.failed_auth_per_user_enabled && userId) {
        const { data: userAttempts } = await supabase?.from('failed_auth_tracking')?.select('*')?.eq('user_id', userId)?.gte('attempt_timestamp', windowStart)?.order('attempt_timestamp', { ascending: false });

        if (userAttempts && userAttempts?.length >= config?.failed_auth_threshold) {
          await this.createPatternIncident({
            patternType: 'failed_auth',
            severity: 'high',
            title: `Multiple Failed Login Attempts Detected`,
            description: `${userAttempts?.length} failed login attempts detected for user in the last ${config?.failed_auth_window_minutes} minutes`,
            affectedUserIds: [userId],
            triggerData: {
              attempts: userAttempts?.length,
              threshold: config?.failed_auth_threshold,
              window_minutes: config?.failed_auth_window_minutes,
              recent_attempts: userAttempts?.slice(0, 5)
            },
            riskScore: 85
          });
        }
      }

      // Check per-IP pattern
      if (config?.failed_auth_per_ip_enabled && ipAddress) {
        const { data: ipAttempts } = await supabase?.from('failed_auth_tracking')?.select('*')?.eq('ip_address', ipAddress)?.gte('attempt_timestamp', windowStart)?.order('attempt_timestamp', { ascending: false });

        if (ipAttempts && ipAttempts?.length >= config?.failed_auth_threshold) {
          const uniqueUsers = [...new Set(ipAttempts?.map(a => a?.user_id)?.filter(Boolean))];
          
          await this.createPatternIncident({
            patternType: 'failed_auth',
            severity: 'critical',
            title: `Brute Force Attack Detected from IP ${ipAddress}`,
            description: `${ipAttempts?.length} failed login attempts from IP ${ipAddress} targeting ${uniqueUsers?.length} user(s) in the last ${config?.failed_auth_window_minutes} minutes`,
            affectedUserIds: uniqueUsers,
            triggerData: {
              ip_address: ipAddress,
              attempts: ipAttempts?.length,
              threshold: config?.failed_auth_threshold,
              targeted_users: uniqueUsers?.length,
              window_minutes: config?.failed_auth_window_minutes
            },
            riskScore: 95
          });
        }
      }
    } catch (error) {
      console.error('Error checking failed auth pattern:', error);
    }
  },

  // ============================================
  // LOCATION ANOMALY DETECTION
  // ============================================

  async checkLocationAnomaly(userId, ipAddress, location) {
    try {
      const config = await this.getPatternConfig();
      if (!config?.location_detection_enabled) return;

      // Check if IP is whitelisted
      const { data: whitelisted } = await supabase?.from('ip_whitelist')?.select('*')?.eq('user_id', userId)?.eq('ip_address', ipAddress)?.eq('is_active', true)?.single();

      if (whitelisted) return; // Skip detection for whitelisted IPs

      // Get user's location baselines
      const { data: baselines } = await supabase?.from('user_location_baselines')?.select('*')?.eq('user_id', userId)?.order('login_count', { ascending: false });

      if (!baselines || baselines?.length === 0) {
        // First login - create baseline
        await this.updateLocationBaseline(userId, ipAddress, location);
        return;
      }

      // Check for new country
      const existingCountries = baselines?.map(b => b?.country_code)?.filter(Boolean);
      if (config?.location_new_country_alert && location?.country_code && !existingCountries?.includes(location?.country_code)) {
        await this.createPatternIncident({
          patternType: 'location_anomaly',
          severity: 'medium',
          title: `Login from New Country Detected`,
          description: `User logged in from ${location?.country_code} (${location?.city || 'Unknown City'}) for the first time`,
          affectedUserIds: [userId],
          triggerData: {
            new_country: location?.country_code,
            new_city: location?.city,
            ip_address: ipAddress,
            known_countries: existingCountries
          },
          riskScore: 60
        });
      }

      // Calculate distance from trusted locations
      if (location?.latitude && location?.longitude) {
        const trustedLocations = baselines?.filter(b => b?.is_trusted && b?.latitude && b?.longitude);
        
        if (trustedLocations?.length > 0) {
          const distances = trustedLocations?.map(trusted => ({
            location: `${trusted?.city}, ${trusted?.country_code}`,
            distance: this.calculateDistance(
              location?.latitude,
              location?.longitude,
              trusted?.latitude,
              trusted?.longitude
            )
          }));

          const minDistance = Math.min(...distances?.map(d => d?.distance));

          if (minDistance > config?.location_distance_threshold_km) {
            const riskScore = Math.min(100, Math.floor((minDistance / config?.location_distance_threshold_km) * 50) + 50);

            if (riskScore >= config?.location_risk_threshold) {
              await this.createPatternIncident({
                patternType: 'location_anomaly',
                severity: riskScore >= 90 ? 'high' : 'medium',
                title: `Unusual Login Location Detected`,
                description: `Login detected ${Math.floor(minDistance)} km away from typical locations`,
                affectedUserIds: [userId],
                triggerData: {
                  current_location: `${location?.city || 'Unknown'}, ${location?.country_code || 'Unknown'}`,
                  ip_address: ipAddress,
                  distance_from_trusted: Math.floor(minDistance),
                  threshold_km: config?.location_distance_threshold_km,
                  trusted_locations: trustedLocations?.map(t => `${t?.city}, ${t?.country_code}`)
                },
                riskScore
              });
            }
          }
        }
      }

      // Update location baseline
      await this.updateLocationBaseline(userId, ipAddress, location);
    } catch (error) {
      console.error('Error checking location anomaly:', error);
    }
  },

  async updateLocationBaseline(userId, ipAddress, location) {
    try {
      if (!location?.country_code) return;

      const { data: existing } = await supabase?.from('user_location_baselines')?.select('*')?.eq('user_id', userId)?.eq('country_code', location?.country_code)?.eq('city', location?.city || '')?.eq('ip_address', ipAddress)?.single();

      if (existing) {
        // Update existing baseline
        await supabase?.from('user_location_baselines')?.update({
            login_count: existing?.login_count + 1,
            last_seen_at: new Date()?.toISOString(),
            is_trusted: existing?.login_count + 1 >= 5 // Trust after 5 logins
          })?.eq('id', existing?.id);
      } else {
        // Create new baseline
        await supabase?.from('user_location_baselines')?.insert({
            user_id: userId,
            country_code: location?.country_code,
            city: location?.city || '',
            ip_address: ipAddress,
            latitude: location?.latitude,
            longitude: location?.longitude,
            login_count: 1,
            is_trusted: false,
            risk_score: 30 // Initial risk score for new location
          });
      }
    } catch (error) {
      console.error('Error updating location baseline:', error);
    }
  },

  calculateDistance(lat1, lon1, lat2, lon2) {
    // Haversine formula for distance calculation
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  },

  toRad(degrees) {
    return degrees * (Math.PI / 180);
  },

  // ============================================
  // BULK PERMISSION CHANGE DETECTION
  // ============================================

  async trackPermissionChange(changedByUserId, targetUserId, changeType, oldValue, newValue, metadata = {}) {
    try {
      const batchId = metadata?.batchId || null;
      const isPrivilegeEscalation = this.isPrivilegeEscalation(changeType, oldValue, newValue);

      const { error } = await supabase?.from('permission_change_patterns')?.insert({
          changed_by_user_id: changedByUserId,
          target_user_id: targetUserId,
          change_type: changeType,
          old_value: oldValue,
          new_value: newValue,
          batch_id: batchId,
          is_privilege_escalation: isPrivilegeEscalation,
          risk_score: isPrivilegeEscalation ? 80 : 40,
          metadata,
          change_timestamp: new Date()?.toISOString()
        });

      if (error) throw error;

      // Check for bulk patterns
      await this.checkBulkPermissionPattern(changedByUserId);
    } catch (error) {
      console.error('Error tracking permission change:', error);
      throw error;
    }
  },

  isPrivilegeEscalation(changeType, oldValue, newValue) {
    if (changeType !== 'role_change') return false;

    const roleHierarchy = { member: 1, admin: 2, owner: 3 };
    const oldLevel = roleHierarchy?.[oldValue] || 0;
    const newLevel = roleHierarchy?.[newValue] || 0;

    return newLevel > oldLevel;
  },

  async checkBulkPermissionPattern(changedByUserId) {
    try {
      const config = await this.getPatternConfig();
      if (!config?.bulk_permission_enabled) return;

      const windowStart = new Date(Date.now() - config?.bulk_permission_window_minutes * 60000)?.toISOString();

      // Get recent permission changes by this user
      const { data: recentChanges } = await supabase?.from('permission_change_patterns')?.select('*')?.eq('changed_by_user_id', changedByUserId)?.gte('change_timestamp', windowStart)?.order('change_timestamp', { ascending: false });

      if (!recentChanges || recentChanges?.length < config?.bulk_permission_threshold) return;

      // Check for privilege escalation pattern
      const escalations = recentChanges?.filter(c => c?.is_privilege_escalation);
      const affectedUsers = [...new Set(recentChanges?.map(c => c?.target_user_id)?.filter(Boolean))];

      if (escalations?.length >= 2 && config?.privilege_escalation_detection) {
        await this.createPatternIncident({
          patternType: 'bulk_permission',
          severity: 'critical',
          title: `Multiple Privilege Escalations Detected`,
          description: `${escalations?.length} privilege escalations performed in ${config?.bulk_permission_window_minutes} minutes, affecting ${affectedUsers?.length} user(s)`,
          affectedUserIds: [changedByUserId, ...affectedUsers],
          triggerData: {
            changed_by: changedByUserId,
            escalation_count: escalations?.length,
            affected_users: affectedUsers?.length,
            window_minutes: config?.bulk_permission_window_minutes,
            changes: escalations?.map(e => ({
              target_user: e?.target_user_id,
              old_role: e?.old_value,
              new_role: e?.new_value,
              timestamp: e?.change_timestamp
            }))
          },
          riskScore: 95
        });
      } else if (recentChanges?.length >= config?.bulk_permission_threshold) {
        await this.createPatternIncident({
          patternType: 'bulk_permission',
          severity: 'high',
          title: `Bulk Permission Changes Detected`,
          description: `${recentChanges?.length} permission changes performed in ${config?.bulk_permission_window_minutes} minutes, affecting ${affectedUsers?.length} user(s)`,
          affectedUserIds: [changedByUserId, ...affectedUsers],
          triggerData: {
            changed_by: changedByUserId,
            change_count: recentChanges?.length,
            affected_users: affectedUsers?.length,
            threshold: config?.bulk_permission_threshold,
            window_minutes: config?.bulk_permission_window_minutes,
            change_types: [...new Set(recentChanges?.map(c => c?.change_type))]
          },
          riskScore: 75
        });
      }
    } catch (error) {
      console.error('Error checking bulk permission pattern:', error);
    }
  },

  // ============================================
  // INCIDENT CREATION & ESCALATION
  // ============================================

  async createPatternIncident({ patternType, severity, title, description, affectedUserIds, triggerData, riskScore }) {
    try {
      // Check if similar incident already exists (within last hour)
      const oneHourAgo = new Date(Date.now() - 60 * 60000)?.toISOString();
      const { data: existingIncidents } = await supabase?.from('pattern_detection_incidents')?.select('*, incident:service_incidents(*)')?.eq('pattern_type', patternType)?.gte('detection_timestamp', oneHourAgo)?.order('detection_timestamp', { ascending: false })?.limit(1);

      if (existingIncidents && existingIncidents?.length > 0) {
        const existing = existingIncidents?.[0];
        // Update existing incident instead of creating duplicate
        await supabase?.from('pattern_detection_incidents')?.update({
            trigger_data: triggerData,
            risk_score: riskScore,
            affected_user_ids: affectedUserIds
          })?.eq('id', existing?.id);
        
        return existing;
      }

      // Create service incident
      const { data: incident, error: incidentError } = await supabase?.from('service_incidents')?.insert({
          service_name: 'security',
          severity,
          status: 'open',
          title,
          description,
          detected_at: new Date()?.toISOString(),
          metadata: {
            pattern_type: patternType,
            risk_score: riskScore,
            affected_users: affectedUserIds,
            trigger_data: triggerData
          }
        })?.select()?.single();

      if (incidentError) throw incidentError;

      // Create pattern detection incident record
      const { data: patternIncident, error: patternError } = await supabase?.from('pattern_detection_incidents')?.insert({
          incident_id: incident?.id,
          pattern_type: patternType,
          affected_user_ids: affectedUserIds,
          trigger_data: triggerData,
          risk_score: riskScore,
          detection_timestamp: new Date()?.toISOString()
        })?.select()?.single();

      if (patternError) throw patternError;

      // Check for auto-escalation
      await this.checkAutoEscalation(incident, patternIncident);

      return patternIncident;
    } catch (error) {
      console.error('Error creating pattern incident:', error);
      throw error;
    }
  },

  async checkAutoEscalation(incident, patternIncident) {
    try {
      const config = await this.getPatternConfig();
      if (!config?.auto_escalation_enabled) return;

      // Check if severity meets threshold
      const severityLevels = { low: 1, medium: 2, high: 3, critical: 4 };
      const incidentLevel = severityLevels?.[incident?.severity] || 0;
      const thresholdLevel = severityLevels?.[config?.escalation_severity_threshold] || 3;

      if (incidentLevel < thresholdLevel) return;

      // Trigger escalation
      await this.triggerEscalation(incident, patternIncident, config);
    } catch (error) {
      console.error('Error checking auto-escalation:', error);
    }
  },

  async triggerEscalation(incident, patternIncident, config) {
    try {
      const channels = [];
      const notifications = [];

      // Get recipients
      const recipients = config?.escalation_recipients || [];
      if (recipients?.length === 0) {
        // Default to current user
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          const { data: profile } = await supabase?.from('user_profiles')?.select('email, full_name')?.eq('id', user?.id)?.single();
          if (profile) {
            recipients?.push({ email: profile?.email, phone: null, name: profile?.full_name });
          }
        }
      }

      // Send SMS alerts
      if (config?.escalation_sms_enabled) {
        const smsRecipients = recipients?.filter(r => r?.phone);
        for (const recipient of smsRecipients) {
          try {
            await this.sendEscalationSMS(recipient?.phone, incident, patternIncident);
            channels?.push('sms');
          } catch (error) {
            console.error('Failed to send SMS escalation:', error);
          }
        }
      }

      // Send Email alerts
      if (config?.escalation_email_enabled) {
        const emailRecipients = recipients?.filter(r => r?.email);
        for (const recipient of emailRecipients) {
          try {
            await this.sendEscalationEmail(recipient?.email, incident, patternIncident);
            channels?.push('email');
          } catch (error) {
            console.error('Failed to send email escalation:', error);
          }
        }
      }

      // Update pattern incident with escalation info
      await supabase?.from('pattern_detection_incidents')?.update({
          auto_escalated: true,
          escalation_sent_at: new Date()?.toISOString(),
          escalation_channels: [...new Set(channels)]
        })?.eq('id', patternIncident?.id);

      // Update service incident escalation count
      await alertManagementService?.triggerEscalation(incident?.id);
    } catch (error) {
      console.error('Error triggering escalation:', error);
      throw error;
    }
  },

  async sendEscalationSMS(phoneNumber, incident, patternIncident) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) throw new Error('Not authenticated');

      const message = `🚨 SECURITY ALERT [${incident?.severity?.toUpperCase()}]: ${incident?.title}. Incident ID: ${incident?.id?.slice(0, 8)}. Check dashboard immediately.`;

      const response = await fetch(`${EDGE_FUNCTION_URL}/send-security-alert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          alertType: 'pattern_detection',
          severity: incident?.severity,
          title: incident?.title,
          description: incident?.description,
          smsEnabled: true,
          emailEnabled: false,
          phoneNumber,
          metadata: {
            incident_id: incident?.id,
            pattern_type: patternIncident?.pattern_type,
            risk_score: patternIncident?.risk_score
          }
        })
      });

      if (!response?.ok) {
        const error = await response?.json();
        throw new Error(error?.error || 'Failed to send SMS escalation');
      }

      return await response?.json();
    } catch (error) {
      console.error('Error sending escalation SMS:', error);
      throw error;
    }
  },

  async sendEscalationEmail(email, incident, patternIncident) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${EDGE_FUNCTION_URL}/send-security-alert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          alertType: 'pattern_detection',
          severity: incident?.severity,
          title: incident?.title,
          description: incident?.description,
          smsEnabled: false,
          emailEnabled: true,
          email,
          metadata: {
            incident_id: incident?.id,
            pattern_type: patternIncident?.pattern_type,
            risk_score: patternIncident?.risk_score,
            trigger_data: patternIncident?.trigger_data
          }
        })
      });

      if (!response?.ok) {
        const error = await response?.json();
        throw new Error(error?.error || 'Failed to send email escalation');
      }

      return await response?.json();
    } catch (error) {
      console.error('Error sending escalation email:', error);
      throw error;
    }
  },

  // ============================================
  // IP WHITELIST MANAGEMENT
  // ============================================

  async getIPWhitelist() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('ip_whitelist')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching IP whitelist:', error);
      throw error;
    }
  },

  async addIPToWhitelist(ipAddress, description) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('ip_whitelist')?.insert({
          user_id: user?.id,
          ip_address: ipAddress,
          description,
          is_active: true,
          created_by: user?.id
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding IP to whitelist:', error);
      throw error;
    }
  },

  async removeIPFromWhitelist(whitelistId) {
    try {
      const { error } = await supabase?.from('ip_whitelist')?.delete()?.eq('id', whitelistId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error removing IP from whitelist:', error);
      throw error;
    }
  },

  async toggleIPWhitelist(whitelistId, isActive) {
    try {
      const { data, error } = await supabase?.from('ip_whitelist')?.update({ is_active: isActive })?.eq('id', whitelistId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error toggling IP whitelist:', error);
      throw error;
    }
  },

  // ============================================
  // STATISTICS & MONITORING
  // ============================================

  async getPatternStatistics(days = 30) {
    try {
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000)?.toISOString();

      const { data: incidents } = await supabase?.from('pattern_detection_incidents')?.select('*')?.gte('detection_timestamp', startDate);

      const stats = {
        total_incidents: incidents?.length || 0,
        by_pattern_type: {
          failed_auth: 0,
          location_anomaly: 0,
          bulk_permission: 0
        },
        by_severity: {
          low: 0,
          medium: 0,
          high: 0,
          critical: 0
        },
        auto_escalated: 0,
        avg_risk_score: 0
      };

      if (incidents && incidents?.length > 0) {
        incidents?.forEach(incident => {
          if (incident?.pattern_type) {
            stats.by_pattern_type[incident?.pattern_type] = (stats?.by_pattern_type?.[incident?.pattern_type] || 0) + 1;
          }
          if (incident?.auto_escalated) {
            stats.auto_escalated++;
          }
        });

        // Get severity from linked incidents
        const incidentIds = incidents?.map(i => i?.incident_id)?.filter(Boolean);
        if (incidentIds?.length > 0) {
          const { data: serviceIncidents } = await supabase?.from('service_incidents')?.select('severity')?.in('id', incidentIds);
          
          serviceIncidents?.forEach(si => {
            if (si?.severity) {
              stats.by_severity[si?.severity]++;
            }
          });
        }

        const totalRisk = incidents?.reduce((sum, i) => sum + (i?.risk_score || 0), 0);
        stats.avg_risk_score = Math.round(totalRisk / incidents?.length);
      }

      return stats;
    } catch (error) {
      console.error('Error fetching pattern statistics:', error);
      throw error;
    }
  },

  async getRecentPatternIncidents(limit = 10) {
    try {
      const { data, error } = await supabase?.from('pattern_detection_incidents')?.select(`
          *,
          incident:service_incidents(*)
        `)?.order('detection_timestamp', { ascending: false })?.limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching recent pattern incidents:', error);
      throw error;
    }
  }
};

export default patternDetectionService;